/**
 * Single source for deal lifecycle: pipeline order, API `value`, UI label.
 * Persisted/API values must stay stable (e.g. "Quoted"); labels are display-only.
 */
export const DEAL_STAGES = [
  { value: 'Demand', label: 'Demand' },
  { value: 'Quoted', label: 'Quote Issued' },
  { value: 'Approved', label: 'Approved' },
  // Partial: some tranches invoiced, deal still ongoing, more cash to come.
  { value: 'Partial', label: 'Partial' },
  { value: 'Invoiced', label: 'Invoiced' },
  { value: 'Paid', label: 'Paid' },
  { value: 'Rejected', label: 'Rejected' },
  { value: 'Archived', label: 'Archived' },
];

/** API values in pipeline order (matches backend deal_stages.DEAL_STAGE_VALUES_ORDERED). */
export const DEAL_STAGE_VALUES_ORDERED = DEAL_STAGES.map((s) => s.value);

/**
 * Deal Library "Active" quick filter — pipeline order.
 * Mirror `deal_stages.deal_library_active_quick_filter_stages_ordered()` / `DEAL_LIBRARY_ACTIVE_QUICK_FILTER_STAGES`.
 */
export const DEAL_LIBRARY_ACTIVE_QUICK_FILTER_STAGES_ORDERED = Object.freeze([
  'Approved',
  'Partial',
  'Invoiced',
]);

export const DEAL_LIBRARY_ACTIVE_QUICK_FILTER_STAGES = new Set(DEAL_LIBRARY_ACTIVE_QUICK_FILTER_STAGES_ORDERED);

for (const s of DEAL_LIBRARY_ACTIVE_QUICK_FILTER_STAGES_ORDERED) {
  if (!DEAL_STAGE_VALUES_ORDERED.includes(s)) {
    throw new Error(`DEAL_LIBRARY_ACTIVE_QUICK_FILTER_STAGES_ORDERED entry "${s}" is not a known stage`);
  }
}

/**
 * Deal Library "Upcoming" quick filter — pipeline order.
 * Mirror `deal_stages.deal_library_upcoming_quick_filter_stages_ordered()`.
 */
export const DEAL_LIBRARY_UPCOMING_QUICK_FILTER_STAGES_ORDERED = Object.freeze(['Demand', 'Quoted']);

export const DEAL_LIBRARY_UPCOMING_QUICK_FILTER_STAGES = new Set(
  DEAL_LIBRARY_UPCOMING_QUICK_FILTER_STAGES_ORDERED,
);

for (const s of DEAL_LIBRARY_UPCOMING_QUICK_FILTER_STAGES_ORDERED) {
  if (!DEAL_STAGE_VALUES_ORDERED.includes(s)) {
    throw new Error(`DEAL_LIBRARY_UPCOMING_QUICK_FILTER_STAGES_ORDERED entry "${s}" is not a known stage`);
  }
}

/** Deal Library "Active" CTA — mirror `deal_stages.deal_matches_deal_library_active_quick_filter`. */
export function dealMatchesDealLibraryActiveQuickFilter(stageValue, invoicedPercentage) {
  const s = String(stageValue == null || stageValue === '' ? 'Demand' : stageValue).trim();
  if (!DEAL_LIBRARY_ACTIVE_QUICK_FILTER_STAGES.has(s)) return false;
  let inv = 0;
  if (invoicedPercentage != null && invoicedPercentage !== '') {
    const n = Number(invoicedPercentage);
    inv = Number.isFinite(n) ? n : 0;
  }
  // Match backend: treat 100 as fully invoiced (same scale as Deal Library Inv. % column).
  return inv < 100;
}

/** Deal Library "Upcoming" CTA — mirror `deal_stages.deal_matches_deal_library_upcoming_quick_filter`. */
export function dealMatchesDealLibraryUpcomingQuickFilter(stageValue) {
  const s = String(stageValue == null || stageValue === '' ? 'Demand' : stageValue).trim();
  return DEAL_LIBRARY_UPCOMING_QUICK_FILTER_STAGES.has(s);
}

export function getStageLabel(stageValue) {
  if (stageValue == null || stageValue === '') return 'Demand';
  const row = DEAL_STAGES.find((s) => s.value === stageValue);
  return row ? row.label : String(stageValue);
}

/** TopBar / bordered selector chips */
export const STAGE_TRIGGER_COLORS = {
  Demand: 'bg-gray-100 text-gray-700 border-gray-200',
  Quoted: 'bg-orange-50 text-orange-700 border-orange-200',
  Approved: 'bg-blue-50 text-blue-700 border-blue-200',
  Partial: 'bg-teal-50 text-teal-700 border-teal-200',
  Invoiced: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  Paid: 'bg-green-50 text-green-700 border-green-200',
  Rejected: 'bg-red-50 text-red-700 border-red-200',
  Archived: 'bg-gray-200 text-gray-700 border-gray-300',
};

/** Compact badges (e.g. load dialog table) */
export const STAGE_BADGE_COLORS = {
  Demand: 'bg-gray-100 text-gray-700',
  Quoted: 'bg-orange-50 text-orange-700',
  Approved: 'bg-blue-50 text-blue-700',
  Partial: 'bg-teal-50 text-teal-700',
  Invoiced: 'bg-indigo-50 text-indigo-700',
  Paid: 'bg-green-50 text-green-700',
  Rejected: 'bg-red-50 text-red-700',
  Archived: 'bg-gray-200 text-gray-700',
};

/** Hardcoded pipeline weights (Phase 2). Mirror backend deal_stages.STAGE_WIN_PROBABILITY. */
export const STAGE_WIN_PROBABILITY = {
  Demand: 0.2,
  Quoted: 0.4,
  Approved: 0.7,
  Partial: 0.85,
  Invoiced: 0.9,
  Paid: 1.0,
  Rejected: 0,
  Archived: 0,
};

if (new Set(Object.keys(STAGE_WIN_PROBABILITY)).size !== DEAL_STAGE_VALUES_ORDERED.length) {
  throw new Error('STAGE_WIN_PROBABILITY keys must match DEAL_STAGE_VALUES_ORDERED');
}
for (const v of DEAL_STAGE_VALUES_ORDERED) {
  if (!(v in STAGE_WIN_PROBABILITY)) throw new Error(`Missing STAGE_WIN_PROBABILITY for ${v}`);
}

export function getStageWinProbability(stageValue) {
  if (stageValue == null || stageValue === '') return STAGE_WIN_PROBABILITY.Demand;
  return STAGE_WIN_PROBABILITY[stageValue] ?? STAGE_WIN_PROBABILITY.Demand;
}
